// Where the user writes a message of the text channel shown: lines that grow as the text does (Enter
// sends, Shift+Enter breaks the line), the message it answers above them, the images it takes — picked,
// pasted or dropped, each with how its upload goes — and the emoji picker. `@` offers the meeting's users
// to name, `:` emoji by name; Ctrl+B, Ctrl+I and Ctrl+U make the selection bold, italic, underlined;
// ArrowUp in an empty box edits the user's last message. What is written stays the channel's draft.
import { useLayoutEffect, useRef, useState } from "preact/hooks";
import { answer, attach, detach, draftOf, editing, lastOwn, say, typed } from "../chat.ts";
import { emojiOf, loadEmoji, searchEmoji, tookEmoji } from "../emoji.ts";
import { changed, channelName, channelOf, nameOf, state, type Attachment } from "../store.ts";
import { Avatar } from "./Avatar.tsx";
import { EmojiPicker } from "./EmojiPicker.tsx";
import { Icon, IconButton } from "./Icon.tsx";

/** The most lines' height the box grows to, px. */
const TALLEST = 240;
/** The formats the server takes, for the file picker. */
const ACCEPT = "image/png,image/jpeg,image/gif,image/webp";

/** What the box offers: users to name or emoji, for the text from `from` to the caret. */
type Offer = { kind: "user" | "emoji"; from: number; items: { key: string; label: string; value: string; user?: number }[]; index: number };

export function Composer() {
  const channel = state.view.channel;
  const d = draftOf(channel);
  const field = useRef<HTMLTextAreaElement>(null);
  const picker = useState(false);
  const [offer, setOffer] = useState<Offer | null>(null);
  const files = useRef<HTMLInputElement>(null);
  const c = channelOf(channel, "text");
  // The box's height follows its text.
  const fit = () => {
    const f = field.current;
    if (f === null) return;
    // Empty, it is a line high (a placeholder would measure as it wraps, before the box has its width).
    f.style.height = "auto";
    if (f.value !== "") f.style.height = `${Math.min(TALLEST, f.scrollHeight)}px`;
  };
  useLayoutEffect(fit);
  const submit = async () => {
    const f = field.current!;
    // Shortcodes written before the emoji came: their emoji now, the emoji loaded first if need be.
    const codes = /:([a-z0-9_+-]{2,40}):/gi;
    if (codes.test(f.value)) await loadEmoji().catch(() => null);
    say(f.value.replace(codes, (all, code: string) => emojiOf(code) ?? all));
    if (state.drafts.get(channel)?.text === "") f.value = "";
    setOffer(null);
    fit();
  };
  /** The box's text `text`, the caret at `caret`: the draft follows. */
  const put = (text: string, caret: number) => {
    const f = field.current!;
    f.value = text;
    f.setSelectionRange(caret, caret);
    d.text = text;
    fit();
    f.focus();
  };
  const offered = () => {
    const f = field.current!;
    const before = f.value.slice(0, f.selectionStart ?? 0);
    // A shortcode written whole becomes its emoji.
    const whole = /:([a-z0-9_+-]{2,40}):$/i.exec(before);
    if (whole !== null) {
      const e = emojiOf(whole[1]);
      if (e !== null) {
        const at = before.length - whole[0].length;
        put(f.value.slice(0, at) + e + f.value.slice(before.length), at + e.length);
        return setOffer(null);
      }
    }
    const user = /(?:^|\s)@([\p{L}\p{N}_.-]{0,32})$/u.exec(before);
    if (user !== null) {
      const q = user[1].toLowerCase();
      const known = [...new Set([...state.online, ...state.names.keys()])].filter((u) => u !== state.user);
      const items = known
        .filter((u) => nameOf(u).toLowerCase().includes(q))
        .sort((a, b) => Number(state.online.has(b)) - Number(state.online.has(a)) || nameOf(a).localeCompare(nameOf(b), "ru"))
        .slice(0, 8)
        .map((u) => ({ key: String(u), label: nameOf(u), value: `@${nameOf(u)} `, user: u }));
      return setOffer(items.length === 0 ? null : { kind: "user", from: before.length - user[1].length - 1, items, index: 0 });
    }
    const emoji = /(?:^|\s):([\p{L}\p{N}_+-]{2,32})$/u.exec(before);
    if (emoji !== null) {
      void loadEmoji().then(() => changed());
      const items = searchEmoji(emoji[1], 8).map((x) => ({
        key: x.e,
        label: `${x.e}  ${x.codes !== "" ? `:${x.codes.split(" ")[0]}:` : x.name}`,
        value: x.e,
      }));
      return setOffer(items.length === 0 ? null : { kind: "emoji", from: before.length - emoji[1].length - 1, items, index: 0 });
    }
    setOffer(null);
  };
  const take = (o: Offer, i: number) => {
    const f = field.current!;
    const item = o.items[i];
    const caret = f.selectionStart ?? f.value.length;
    const text = f.value.slice(0, o.from) + item.value + f.value.slice(caret);
    if (item.user !== undefined && !d.named.some(([n]) => n === nameOf(item.user!))) d.named = [...d.named, [nameOf(item.user), item.user]];
    if (o.kind === "emoji") tookEmoji(item.value);
    put(text, o.from + item.value.length);
    setOffer(null);
  };
  /** The selection between marks `mark` (`**`, `*`, `__`). */
  const wrap = (mark: string) => {
    const f = field.current!;
    const [a, b] = [f.selectionStart ?? 0, f.selectionEnd ?? 0];
    const text = f.value.slice(0, a) + mark + f.value.slice(a, b) + mark + f.value.slice(b);
    put(text, b + mark.length * 2);
    if (a !== b) f.setSelectionRange(a + mark.length, b + mark.length);
  };
  const keyDown = (e: KeyboardEvent & { currentTarget: HTMLTextAreaElement }) => {
    if (offer !== null) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        const n = offer.items.length;
        return setOffer({ ...offer, index: (offer.index + (e.key === "ArrowDown" ? 1 : n - 1)) % n });
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        return take(offer, offer.index);
      }
      if (e.key === "Escape") {
        e.preventDefault();
        return setOffer(null);
      }
    }
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      return void submit();
    }
    if (e.key === "Escape" && d.reply !== null) return answer(null);
    if (e.key === "ArrowUp" && e.currentTarget.value === "") {
      const m = lastOwn();
      if (m !== null) {
        e.preventDefault();
        editing(m.seq);
      }
      return;
    }
    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
      const mark = e.key === "b" || e.key === "и" ? "**" : e.key === "i" || e.key === "ш" ? "*" : e.key === "u" || e.key === "г" ? "__" : "";
      if (mark !== "") {
        e.preventDefault();
        wrap(mark);
      }
    }
  };
  const paste = (e: ClipboardEvent) => {
    const images = [...(e.clipboardData?.files ?? [])].filter((f) => f.type.startsWith("image/"));
    if (images.length === 0) return;
    // An image alone (a screenshot): no text goes in its place.
    if (!(e.clipboardData?.types ?? []).includes("text/plain")) e.preventDefault();
    attach(images);
  };
  const reply = d.reply;
  const ready = d.files.every((f) => f.status !== "failed");
  return (
    <div class="composer-wrap">
      {offer !== null && (
        <div class="offer" role="listbox">
          <div class="offer-head">{offer.kind === "user" ? "Участники" : "Эмодзи"}</div>
          {offer.items.map((item, i) => (
            <button
              key={item.key}
              class={i === offer.index ? "offer-item on" : "offer-item"}
              onMouseDown={(e) => {
                e.preventDefault();
                take(offer, i);
              }}
            >
              {item.user !== undefined && <Avatar id={item.user} size={24} />}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
      <div class="composer">
        {reply !== null && (
          <div class="composer-reply">
            <Icon name="reply" size={16} />
            <span>
              Ответ <b>{nameOf(reply.from)}</b>
            </span>
            <IconButton icon="close" title="Не отвечать" onClick={() => answer(null)} />
          </div>
        )}
        {d.files.length > 0 && (
          <div class="attachments">
            {d.files.map((a) => (
              <AttachmentCard key={a.key} a={a} />
            ))}
          </div>
        )}
        <div class="composer-row">
          <IconButton icon="image" title="Прикрепить изображение" onClick={() => files.current?.click()} />
          <input
            ref={files}
            type="file"
            accept={ACCEPT}
            multiple
            hidden
            onChange={(e) => {
              attach([...(e.currentTarget.files ?? [])]);
              e.currentTarget.value = "";
            }}
          />
          <textarea
            ref={field}
            rows={1}
            defaultValue={d.text}
            placeholder={`Сообщение в #${c === null ? "" : channelName(c)}`}
            enterkeyhint="send"
            onInput={(e) => {
              d.text = e.currentTarget.value;
              typed();
              fit();
              offered();
            }}
            onKeyDown={keyDown}
            onClick={offered}
            onPaste={paste}
            onBlur={() => setTimeout(() => setOffer(null), 150)}
          />
          <div class="composer-emoji">
            <IconButton icon="smile" title="Эмодзи" onClick={() => picker[1](!picker[0])} />
            {picker[0] && (
              <EmojiPicker
                onPick={(e) => {
                  const f = field.current!;
                  const [a, b] = [f.selectionStart ?? f.value.length, f.selectionEnd ?? f.value.length];
                  tookEmoji(e);
                  put(f.value.slice(0, a) + e + f.value.slice(b), a + e.length);
                }}
                onClose={() => picker[1](false)}
              />
            )}
          </div>
          <IconButton icon="send" class={ready ? "send" : "send off"} title="Отправить" onClick={() => void submit()} />
        </div>
      </div>
    </div>
  );
}

/** An image of the draft: its picture, how its upload goes, why it did not go up, and taking it out. */
function AttachmentCard({ a }: { a: Attachment }) {
  const title =
    a.status === "failed" ? a.error : a.status === "preparing" ? "готовлю…" : a.status === "uploading" ? "загружаю…" : a.name;
  return (
    <div class={`attachment ${a.status}`} title={title}>
      {a.url !== "" ? <img src={a.url} alt="" /> : <span class="attachment-wait"><Icon name="image" /></span>}
      {(a.status === "uploading" || a.status === "preparing") && (
        <span class="attachment-progress">
          <span style={`width:${Math.round(Math.max(0.05, a.progress) * 100)}%`} />
        </span>
      )}
      {a.status === "failed" && <span class="attachment-error">не загрузилось</span>}
      <span class="attachment-name">{a.name}</span>
      <button class="attachment-remove" title="Убрать" aria-label="Убрать" onClick={() => detach(a.key)}>
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}
